/**
 * LicensorConsolePrimitives — shared UI atoms for LicensorConsole tabs.
 * Extracted from LicensorConsole.jsx to reduce file complexity.
 */
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// ── DataTable ─────────────────────────────────────────────────────────────────
export function formatCell(v) {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (typeof v === 'number') return v.toLocaleString();
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try { return new Date(v).toLocaleString(); } catch { return v; }
  }
  if (typeof v === 'string' && v.length > 80) return v.slice(0, 80) + '…';
  return String(v);
}

export function DataTable({ rows = [], columns = [] }) {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No rows.</div>;
  }
  return (
    <table className="data-table">
      <thead><tr>{columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={columns.reduce((acc, c) => acc + String(r[c] ?? ''), '') || i}>
            {columns.map(c => (
              <td key={c} style={{ fontSize: 12, fontFamily: /Id|Key|Hash|IP|License/.test(c) ? 'monospace' : 'inherit' }}>
                {formatCell(r[c])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── KeyValueGrid ──────────────────────────────────────────────────────────────
export function KeyValueGrid({ obj = {}, skip = [] }) {
  const entries = Object.entries(obj).filter(([k]) => !skip.includes(k) && obj[k] != null && obj[k] !== '');
  if (entries.length === 0) return <div style={{ padding: 16, color: '#94a3b8', fontSize: 12 }}>No data.</div>;
  return (
    <div className="lc-stat-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ background: '#f8fafc', borderRadius: 6, padding: '8px 12px', border: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 2 }}>{k}</div>
          <div style={{ fontSize: 12, fontFamily: /Id|Key|Guid|Hash/.test(k) ? 'monospace' : 'inherit', color: '#0f172a', wordBreak: 'break-word' }}>{formatCell(v)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────
export function Section({ title, sub, children }) {
  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">{title}</div>
          {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
        </div>
      </div>
      <div style={{ padding: 8, overflowX: 'auto' }}>{children}</div>
    </div>
  );
}

// ── SectionHeader ─────────────────────────────────────────────────────────────
export function SectionHeader({ title, badge, onRefresh }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <h2 style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', margin: 0 }}>{title}</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {badge && <span className="card-badge">{badge}</span>}
        {onRefresh && (
          <button className="btn btn-outline" onClick={onRefresh} style={{ fontSize: 12, padding: '4px 12px' }} data-testid="licensor-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        )}
      </div>
    </div>
  );
}

// ── Mini ──────────────────────────────────────────────────────────────────────
export function Mini({ label, value, sub, mono }) {
  return (
    <div className="card" style={{ padding: 7 }}>
      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-word' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── FieldList ─────────────────────────────────────────────────────────────────
export function FieldList({ label, items, required, muted }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: required ? '#dc2626' : muted ? '#94a3b8' : '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {(items || []).map((f) => (
          <span key={f} className={`badge ${required ? 'badge-red' : muted ? 'badge-slate' : 'badge-blue'}`} style={{ fontFamily: 'monospace' }}>{f}</span>
        ))}
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
export function Skeleton({ label }) {
  return (
    <div className="card" style={{ padding: 60, textAlign: 'center' }}>
      <div style={{ width: 28, height: 28, border: '3px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}

// ── ErrorBanner ───────────────────────────────────────────────────────────────
export function ErrorBanner({ error, onRetry }) {
  return (
    <div className="alert-banner red">
      <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, fontSize: 12, color: '#991b1b' }}>
        <strong>Error:</strong> {String(error)}
      </div>
      {onRetry && (
        <button className="btn btn-outline" onClick={onRetry} style={{ fontSize: 12 }}>
          <RefreshCw size={11} /> Retry
        </button>
      )}
    </div>
  );
}
